import { z } from 'zod';
import { OPPORTUNITY_STAGES, OPPORTUNITY_TYPES } from './opportunity.js';
import { MEMBERSHIP_ROLES, PERMISSION_KEYS } from './permissions.js';
import { videoMetaSchema, TARGET_DOWNLOAD } from './video.js';
import { MEDIA_KINDS } from './media.js';
const uuid = z.string().uuid();
const ts = z.string();
export const permissionSetSchema = z.object(Object.fromEntries(PERMISSION_KEYS.map((k) => [k, z.boolean().optional()]))).partial();
export const brandingSchema = z.object({
    primary_color: z.string().optional(),
    accent_color: z.string().optional(),
    logo_url: z.string().url().optional(),
    mode: z.enum(['light', 'dark', 'system']).optional(),
    /** Canvas background preset key (see client lib/backgrounds). Absent/'auto' = understated default. */
    background: z.string().optional(),
    /** Show each client's accent color as a card border on the board (off by default). */
    client_card_border: z.boolean().optional(),
}).partial();
export const NOTE_VISIBILITIES = ['team', 'client'];
/** A per-audition note. 'team' = internal; 'client' = the assigned talent sees it in their portal. */
export const opportunityNoteSchema = z.object({
    id: uuid,
    business_id: uuid,
    opportunity_id: uuid,
    author_id: uuid,
    body: z.string().trim().min(1).max(4000),
    visibility: z.enum(NOTE_VISIBILITIES),
    created_at: ts,
});
/** Realtime invalidation scopes — the payload of a live "bump" (no row data ever transits). */
export const LIVE_SCOPES = ['opportunities', 'notes', 'messages', 'media', 'clients', 'calendar', 'tasks', 'documents', 'notifications', 'bookkeeping'];
export const businessSchema = z.object({
    id: uuid,
    name: z.string(),
    owner_id: uuid,
    slug: z.string().nullable().optional(),
    // `.optional()` (not `.default({})`) keeps input===output so the row type stays zod-3/zod-4
    // dual-compat (a raw DB row / partial select may omit branding).
    branding: brandingSchema.optional(),
    filename_template: z.string().nullable().optional(),
    subscription_status: z.string(),
    trial_ends_at: ts.nullable().optional(),
    /** First moment the subscription went past_due/unpaid; null when healthy. Starts the payment grace window. */
    past_due_since: ts.nullable().optional(),
    seats: z.number().int(),
    created_at: ts,
});
export const membershipSchema = z.object({
    id: uuid,
    business_id: uuid,
    user_id: uuid.nullable(),
    invited_email: z.string().nullable().optional(),
    role: z.enum(MEMBERSHIP_ROLES),
    // `.optional()` (not `.default({})`) keeps input===output (dual zod-3/4); hasPermission already
    // treats a missing set as no extra grants.
    permissions: permissionSetSchema.optional(),
    status: z.enum(['active', 'invited', 'disabled']),
});
/**
 * One extra phone number on a client, beyond the primary `phone`.
 *
 * The label is OPTIONAL by design: an agency often has a second number before it knows whose it is,
 * and forcing a label would mean inventing one. Empty labels normalize to null so a blank and a
 * missing label are the same stored thing.
 */
export const extraPhoneSchema = z.object({
    label: z.string().trim().max(40).nullable().optional(),
    phone: z.string().trim().max(50),
});
/** Ceiling on extra numbers: generous for "up to 3 or 4", low enough to stay a UI list and not a table. */
export const MAX_EXTRA_PHONES = 10;
export const clientSchema = z.object({
    id: uuid,
    business_id: uuid,
    user_id: uuid.nullable().optional(),
    full_name: z.string(),
    email: z.string().email().nullable().optional(),
    phone: z.string().nullable().optional(),
    /** Additional numbers with optional labels; the primary stays on `phone`. */
    extra_phones: z.array(extraPhoneSchema).optional(),
    headshot_url: z.string().nullable().optional(),
    status: z.enum(['active', 'prospect', 'inactive']),
    notes: z.string().nullable().optional(),
    /** Per-client accent color (#rrggbb) — a dot beside the name; optional card border. */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
    /** Soft-archive: when set, the client is off the active roster AND the agency is archived in the talent's portal. */
    archived_at: ts.nullable().optional(),
    created_at: ts,
});
export const clientInputSchema = clientSchema
    .pick({ full_name: true, email: true, phone: true, status: true, notes: true })
    .partial({ email: true, phone: true, status: true, notes: true })
    // Profile values are validated route-side against the business's field schema.
    .extend({ profile: z.record(z.string(), z.unknown()).optional() });
export const opportunitySchema = z.object({
    id: uuid,
    business_id: uuid,
    client_id: uuid,
    title: z.string(),
    project: z.string().nullable().optional(),
    role_name: z.string().nullable().optional(),
    casting_company: z.string().nullable().optional(),
    type: z.enum(OPPORTUNITY_TYPES),
    source: z.enum(['manual', 'email', 'import']),
    stage: z.enum(OPPORTUNITY_STAGES),
    due_date: z.string().nullable().optional(),
    due_time: z.string().nullable().optional(),
    pinned_start: z.string().nullable().optional(),
    pinned_end: z.string().nullable().optional(),
    submit_token: uuid.nullable().optional(),
    submit_open: z.boolean().optional(),
    filename_template: z.string().nullable().optional(),
    booking_amount: z.union([z.number(), z.string()]).nullable().optional(),
    booking_commission_pct: z.union([z.number(), z.string()]).nullable().optional(),
    booking_notes: z.string().nullable().optional(),
    /** Per-audition download size ceiling (bytes); downloads compress to <= this. */
    target_download_bytes: z.number().int().nullable().optional(),
    submitted: z.boolean(),
    submitted_at: ts.nullable().optional(),
    /** The AGENCY (us) submits this audition, not the talent or their other rep - flagged so it stands out. */
    agency_submits: z.boolean().optional(),
    passed: z.boolean(),
    notes: z.string().nullable().optional(),
    created_at: ts,
    updated_at: ts,
});
export const opportunityInputSchema = z.object({
    client_id: uuid,
    title: z.string().min(1),
    project: z.string().optional(),
    role_name: z.string().optional(),
    casting_company: z.string().optional(),
    type: z.enum(OPPORTUNITY_TYPES).default('self_tape'),
    due_date: z.string().optional(),
    due_time: z.string().optional(),
    notes: z.string().optional(),
    target_download_bytes: TARGET_DOWNLOAD.nullable().optional(),
});
export const mediaSchema = z.object({
    id: uuid,
    business_id: uuid,
    client_id: uuid.nullable().optional(),
    opportunity_id: uuid.nullable().optional(),
    kind: z.enum(MEDIA_KINDS),
    bucket: z.string(),
    path: z.string(),
    file_name: z.string().nullable().optional(),
    mime_type: z.string().nullable().optional(),
    size_bytes: z.number().nullable().optional(),
    review_status: z.enum(['pending', 'approved', 'retake_requested']).nullable().optional(),
    review_note: z.string().nullable().optional(),
    reviewed_at: ts.nullable().optional(),
    sort_order: z.number().int().optional(),
    released_at: ts.nullable().optional(),
    created_at: ts,
}).merge(videoMetaSchema);
// ── In-portal messaging (one thread per client) ─────────────────────────────
export const messageSchema = z.object({
    id: uuid,
    business_id: uuid,
    client_id: uuid,
    sender_id: uuid,
    body: z.string().min(1).max(4000),
    read_at: ts.nullable().optional(),
    created_at: ts,
});
export const messageInputSchema = z.object({ body: z.string().trim().min(1).max(4000) });
//# sourceMappingURL=entities.js.map